import type { UploadWithProgressPayload } from '@components/upload-progress-bar/types.ts';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { CreatorRevision } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CommonCreatorElementsRequest,
    CreatorElement,
    CreatorElementDetailsResponse,
    ItemQuizQuestionWithOptionalQuestionId,
    PostElementRequest,
    PutElementRequest,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/api/creatorElements.api.ts';
import {
    createApiCreatorElement,
    deleteApiCreatorElement,
    getApiCreatorElementById,
    getApiCreatorElements,
    updateApiCreatorElement,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/api/creatorElements.api.ts';
import { INITIAL_UPLOAD_PROGRESS } from '@routes/sidebar/admin-center/creator/___pages___/elements/model/constants.ts';
import type { UploadProgress } from '@routes/sidebar/admin-center/creator/___pages___/elements/model/types.ts';
import {
    ELEMENT_TYPE_PDF_KEY,
    ELEMENT_TYPE_VIDEO_KEY,
    ELEMENT_TYPE_YOU_TUBE_KEY,
} from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/form/constants.ts';
import type { CreatorPathway } from '@routes/sidebar/admin-center/creator/___pages___/pathways/api/creatorPathways.api.ts';
import type { UploadContentResponse } from '@routes/sidebar/admin-center/creator/___shared___/createResource.api.ts';
import {
    createApiPublicResourceWithUploadProgress,
    createApiResourceWithUploadProgress,
} from '@routes/sidebar/admin-center/creator/___shared___/createResource.api.ts';
import { resolveContentUri } from '@shared/model/embededContent.slice.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';
import { isEmpty } from 'lodash';

import { ensureQuestionsTemporaryLocalIds, ensureQuestionTemporaryLocalId } from './utils';

export interface ItemQuizQuestionWithLocalId extends ItemQuizQuestionWithOptionalQuestionId {
    temporaryLocalId: number;
}

interface CreatorElementsError {
    message?: string;
    [k: string]: any;
}

interface CreatorElementsState {
    error: CreatorElementsError | null;
    creatorElements: PaginatedResponse<CreatorElement, EmptyResultsDescription> | null;
    elementQuestions: ItemQuizQuestionWithLocalId[];
    selectedQuestion: ItemQuizQuestionWithLocalId | null;
    selectedElement: CreatorElement | null;
    // use this copy for modal in pathway elements > tab elements, filtered by pathwayType and focus /pathways/search
    modalPathwayElements: CreatorElement[];
    elementRevisions: CreatorRevision[];
    usedInPathways: CreatorPathway[];
    isArchiveElementModalOpened: boolean;
    isArchiveElementFailedModalOpened: boolean;
    isPublishElementModalOpened: boolean;
    isChangeElementTypeModalOpened: boolean;
    isVideoPreviewModalOpened: boolean;
    isYouTubeVideoPreviewModalOpened: boolean;
    uploadedResourcePreviewUrl: string | null;
    uploadedResourcePreviewId: string | null;
    isThumbnailModalOpened: boolean;
    isThumbnailUploaded: boolean;
    uploadedThumbnailUri: string | null;
    uploadedThumbnailId: string | null;
    currentGetRequestId: string | null;
    uploadProgress: UploadProgress;

    isFilePreviewLoading: boolean;
    isGetCreatorElementsPending: boolean;
    isGetCreatorElementPending: boolean;
    isCreateCreatorElementPending: boolean;
    isUpdateCreatorElementPending: boolean;
    isDeleteCreatorElementPending: boolean;
    isUploadResourcePending: boolean;
    isUploadPublicContentPending: boolean;
    isUploadQuestionsCsvModalOpened: boolean;
    uploadedQuestionsCsvFile: File | null;
    uploadedQuestionsCsvFilename: string;
    isQuestionsCsvImportFailed: boolean;
    isQuestionsCsvImportErroneous: boolean;
    questionsCsvIssuesFileContent: string | null;
    questionsCsvImportRejectedCount: number;
    questionsCsvImportSuccessCount: number;

    uploadedTaskCsvFilename: string;
    isTaskCsvImportFailed: boolean;
    isTaskCsvImportErroneous: boolean;
    taskCsvIssuesFileContent: string | null;
    taskCsvImportRejectedCount: number;
    taskCsvImportSuccessCount: number;
    // triggers reopening the Task CSV file picker from the global import failure banner.
    isTaskCsvFilePickerRequested: boolean;

    isQuestionCreatorOpened: boolean;
    isRemoveQuestionModalOpened: boolean;
    isDeleteSubtaskModalOpened: boolean;
    isRatingScaleModalOpened: boolean;
    isTaskPreviewModalOpened: boolean;
}

const initialState: CreatorElementsState = {
    error: null,
    creatorElements: null,
    selectedElement: null,
    selectedQuestion: null,
    elementQuestions: [],
    modalPathwayElements: [],
    elementRevisions: [],
    usedInPathways: [],
    isArchiveElementModalOpened: false,
    isArchiveElementFailedModalOpened: false,
    isPublishElementModalOpened: false,
    isChangeElementTypeModalOpened: false,
    isVideoPreviewModalOpened: false,
    isYouTubeVideoPreviewModalOpened: false,
    uploadedResourcePreviewUrl: null,
    uploadedResourcePreviewId: null,
    isThumbnailModalOpened: false,
    isThumbnailUploaded: false,
    uploadedThumbnailUri: null,
    uploadedThumbnailId: null,
    currentGetRequestId: null,
    uploadProgress: INITIAL_UPLOAD_PROGRESS,

    isFilePreviewLoading: false,
    isGetCreatorElementsPending: false,
    isGetCreatorElementPending: false,
    isCreateCreatorElementPending: false,
    isUpdateCreatorElementPending: false,
    isDeleteCreatorElementPending: false,
    isUploadResourcePending: false,
    isUploadPublicContentPending: false,
    isUploadQuestionsCsvModalOpened: false,
    uploadedQuestionsCsvFile: null,
    uploadedQuestionsCsvFilename: '',
    isQuestionsCsvImportFailed: false,
    isQuestionsCsvImportErroneous: false,
    questionsCsvIssuesFileContent: null,
    questionsCsvImportRejectedCount: 0,
    questionsCsvImportSuccessCount: 0,

    uploadedTaskCsvFilename: '',
    isTaskCsvImportFailed: false,
    isTaskCsvImportErroneous: false,
    taskCsvIssuesFileContent: null,
    taskCsvImportRejectedCount: 0,
    taskCsvImportSuccessCount: 0,
    isTaskCsvFilePickerRequested: false,

    isQuestionCreatorOpened: false,
    isRemoveQuestionModalOpened: false,
    isDeleteSubtaskModalOpened: false,
    isRatingScaleModalOpened: false,
    isTaskPreviewModalOpened: false,
};

export const getCreatorElements = createAsyncThunk<
    PaginatedResponse<CreatorElement, EmptyResultsDescription>,
    { payload: CommonCreatorElementsRequest },
    { rejectValue: CreatorElementsError }
>('creatorElements/getCreatorElements', ({ payload }, { rejectWithValue }) =>
    getApiCreatorElements({ payload })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCreatorElementById = createAsyncThunk<
    CreatorElementDetailsResponse,
    { id: string },
    { rejectValue: CreatorElementsError }
>('creatorElements/getCreatorElementById', ({ id }, { rejectWithValue }) =>
    getApiCreatorElementById({ id })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createCreatorElement = createAsyncThunk<
    CreatorElementDetailsResponse,
    { payload: PostElementRequest },
    { rejectValue: CreatorElementsError }
>('creatorElements/createCreatorElement', ({ payload }, { rejectWithValue }) =>
    createApiCreatorElement({ payload })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateCreatorElement = createAsyncThunk<
    CreatorElementDetailsResponse,
    { id: string; payload: PutElementRequest },
    { rejectValue: CreatorElementsError }
>('creatorElements/updateCreatorElement', ({ id, payload }, { rejectWithValue }) =>
    updateApiCreatorElement({ id, payload })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteCreatorElement = createAsyncThunk<
    any,
    { id: string; comment: string | null },
    { rejectValue: CreatorElementsError }
>('creatorElements/deleteCreatorElement', ({ id, comment }, { rejectWithValue }) =>
    deleteApiCreatorElement({ id, comment })
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createElementResourceWithUploadProgress = createAsyncThunk<
    UploadContentResponse[],
    {
        formData: FormData;
        onUploadProgress?: (progress: number) => void;
        signal?: AbortSignal;
    },
    { rejectValue: string[] }
>('creatorElements/createElementResource', ({ formData, onUploadProgress, signal }, { rejectWithValue }) =>
    createApiResourceWithUploadProgress(formData, onUploadProgress, signal)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createElementThumbnailWithUploadProgress = createAsyncThunk<
    UploadContentResponse[],
    UploadWithProgressPayload,
    { rejectValue: string[] }
>('creatorElements/createElementThumbnail', ({ formData, onUploadProgress, signal }, { rejectWithValue }) =>
    createApiPublicResourceWithUploadProgress(formData, onUploadProgress, signal)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const checkCreatorElementContentReady = createAsyncThunk<
    boolean,
    { id: string },
    { rejectValue: CreatorElementsError }
>('creatorElements/checkCreatorElementContentReady', ({ id }, { rejectWithValue }) =>
    getApiCreatorElementById({ id })
        .then((res) => res.elementAdminInfo.isContentReady)
        .catch((err) => rejectWithValue(err)),
);

const creatorElementsSlice = createSlice({
    name: 'creatorElements',
    initialState,
    reducers: {
        setSelectedElement(state, action: PayloadAction<CreatorElement | null>) {
            state.selectedElement = action.payload;
        },
        setIsArchiveElementModalOpened(state, action: PayloadAction<boolean>) {
            state.isArchiveElementModalOpened = action.payload;
        },
        setIsArchiveElementFailedModalOpened(state, action: PayloadAction<boolean>) {
            state.isArchiveElementFailedModalOpened = action.payload;
        },
        setIsPublishElementModalOpened(state, action: PayloadAction<boolean>) {
            state.isPublishElementModalOpened = action.payload;
        },
        setIsChangeElementTypeModalOpened(state, action: PayloadAction<boolean>) {
            state.isChangeElementTypeModalOpened = action.payload;
        },
        setIsVideoPreviewModalOpened(state, action: PayloadAction<boolean>) {
            state.isVideoPreviewModalOpened = action.payload;
        },
        setIsYouTubeVideoPreviewModalOpened(state, action: PayloadAction<boolean>) {
            state.isYouTubeVideoPreviewModalOpened = action.payload;
        },
        setUploadedResourcePreviewUrl(state, action: PayloadAction<string | null>) {
            state.uploadedResourcePreviewUrl = action.payload;
        },
        setUploadedResourcePreviewId(state, action: PayloadAction<string | null>) {
            state.uploadedResourcePreviewId = action.payload;
        },
        clearUploadedResourcePreview(state) {
            state.uploadedResourcePreviewUrl = null;
            state.uploadedResourcePreviewId = null;
        },
        setIsThumbnailModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isThumbnailModalOpened = action.payload;
        },
        setIsThumbnailUploaded: (state, action: PayloadAction<boolean>) => {
            state.isThumbnailUploaded = action.payload;
        },
        setModalPathwayElements(state, action: PayloadAction<CreatorElement[]>) {
            state.modalPathwayElements = action.payload;
        },
        clearSelectedElement: (state) => {
            state.selectedElement = null;
            state.selectedQuestion = null;
            state.elementQuestions = [];
            state.uploadedThumbnailUri = null;
            state.isThumbnailModalOpened = false;
            state.isThumbnailUploaded = false;
            state.isUploadPublicContentPending = false;
            state.usedInPathways = [];
            state.uploadedResourcePreviewUrl = null;
            state.uploadedResourcePreviewId = null;
        },
        resetModalPathwayElementsState: (state) => {
            state.modalPathwayElements = [];
        },
        setQuestionCreatorOpened(state, action: PayloadAction<boolean>) {
            state.isQuestionCreatorOpened = action.payload;
        },
        addNewQuestion(state, action: PayloadAction<ItemQuizQuestionWithLocalId>) {
            state.elementQuestions.push(ensureQuestionTemporaryLocalId(action.payload));
        },
        addQuestionsFromCsv(state, action: PayloadAction<ItemQuizQuestionWithLocalId[]>) {
            state.elementQuestions = action.payload;
        },
        updateQuestion(state, action: PayloadAction<ItemQuizQuestionWithLocalId>) {
            const questionIndex = state.elementQuestions.findIndex(
                (question) => question.temporaryLocalId === action.payload.temporaryLocalId,
            );

            if (questionIndex !== -1) {
                state.elementQuestions[questionIndex] = action.payload;
            }
        },
        removeQuestion(state, action: PayloadAction<{ temporaryLocalId: number }>) {
            state.elementQuestions = state.elementQuestions.filter(
                (question) => question.temporaryLocalId !== action.payload.temporaryLocalId,
            );
        },
        setSelectedQuestion(state, action: PayloadAction<ItemQuizQuestionWithLocalId | null>) {
            state.selectedQuestion = action.payload;
        },
        setUploadProgress(state, action: PayloadAction<Partial<CreatorElementsState['uploadProgress']>>) {
            state.uploadProgress = {
                ...state.uploadProgress,
                ...action.payload,
            };
        },

        setIsRemoveQuestionModalOpened(state, action: PayloadAction<boolean>) {
            state.isRemoveQuestionModalOpened = action.payload;
        },
        setIsUploadQuestionsCsvModalOpened(state, action: PayloadAction<boolean>) {
            state.isUploadQuestionsCsvModalOpened = action.payload;
        },
        setUploadedQuestionsCsvFile(state, action: PayloadAction<File | null>) {
            state.uploadedQuestionsCsvFile = action.payload;
        },
        setUploadedQuestionsCsvFilename(state, action: PayloadAction<string>) {
            state.uploadedQuestionsCsvFilename = action.payload;
        },
        clearQuestionsCsvImportState(state) {
            state.isQuestionsCsvImportFailed = false;
            state.isQuestionsCsvImportErroneous = false;
            state.questionsCsvIssuesFileContent = null;
            state.questionsCsvImportRejectedCount = 0;
            state.questionsCsvImportSuccessCount = 0;
        },
        setQuestionsCsvImportFailed(state, action: PayloadAction<{ issuesFileContent: string | null } | undefined>) {
            state.isQuestionsCsvImportFailed = true;
            state.isQuestionsCsvImportErroneous = false;
            state.questionsCsvIssuesFileContent = action.payload?.issuesFileContent ?? null;
        },
        setQuestionsCsvImportErroneous(
            state,
            action: PayloadAction<{ issuesFileContent: string; rejectedCount: number; importedCount: number }>,
        ) {
            state.isQuestionsCsvImportErroneous = true;
            state.isQuestionsCsvImportFailed = false;
            state.questionsCsvIssuesFileContent = action.payload.issuesFileContent;
            state.questionsCsvImportRejectedCount = action.payload.rejectedCount;
            state.questionsCsvImportSuccessCount = action.payload.importedCount;
        },

        clearUploadProgress(state) {
            state.uploadProgress = INITIAL_UPLOAD_PROGRESS;
        },

        setIsDeleteSubtaskModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeleteSubtaskModalOpened = action.payload;
        },
        setRatingScaleModalOpened(state, action: PayloadAction<boolean>) {
            state.isRatingScaleModalOpened = action.payload;
        },
        setTaskPreviewModalOpened(state, action: PayloadAction<boolean>) {
            state.isTaskPreviewModalOpened = action.payload;
        },

        setUploadedTaskCsvFilename(state, action: PayloadAction<string>) {
            state.uploadedTaskCsvFilename = action.payload;
        },

        clearTaskCsvImportState(state) {
            state.isTaskCsvImportFailed = false;
            state.isTaskCsvImportErroneous = false;
            state.taskCsvIssuesFileContent = null;
            state.taskCsvImportRejectedCount = 0;
            state.taskCsvImportSuccessCount = 0;
        },

        setTaskCsvImportFailed(state, action: PayloadAction<{ issuesFileContent: string | null } | undefined>) {
            state.isTaskCsvImportFailed = true;
            state.isTaskCsvImportErroneous = false;
            state.taskCsvIssuesFileContent = action.payload?.issuesFileContent ?? null;
        },

        setTaskCsvImportErroneous(
            state,
            action: PayloadAction<{
                issuesFileContent: string;
                rejectedCount: number;
                importedCount: number;
            }>,
        ) {
            state.isTaskCsvImportErroneous = true;
            state.isTaskCsvImportFailed = false;
            state.taskCsvIssuesFileContent = action.payload.issuesFileContent;
            state.taskCsvImportRejectedCount = action.payload.rejectedCount;
            state.taskCsvImportSuccessCount = action.payload.importedCount;
        },

        setIsTaskCsvFilePickerRequested(state, action: PayloadAction<boolean>) {
            state.isTaskCsvFilePickerRequested = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCreatorElements.pending, (state, action) => {
            state.isGetCreatorElementsPending = true;
            state.currentGetRequestId = action.meta.requestId;
            state.error = null;
        });
        builder.addCase(getCreatorElements.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetCreatorElementsPending = false;
            state.currentGetRequestId = null;

            const filters = action.meta.arg.payload.filters;
            const hasFilters = Boolean(filters?.type?.length && filters?.createdFor?.length);
            if (hasFilters) {
                state.modalPathwayElements = action.payload?.data ?? [];
            } else {
                state.creatorElements = action.payload;
            }
        });
        builder.addCase(getCreatorElements.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetCreatorElementsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(getCreatorElementById.pending, (state) => {
            state.isGetCreatorElementPending = true;
            state.error = null;
        });
        builder.addCase(getCreatorElementById.fulfilled, (state, action) => {
            state.isGetCreatorElementPending = false;
            state.selectedElement = action.payload.elementAdminInfo;
            state.elementQuestions = ensureQuestionsTemporaryLocalIds(
                action.payload.elementAdminInfo.itemQuizQuestions,
            );

            state.uploadedThumbnailId = state.selectedElement.thumbnailId;
            state.elementRevisions = action.payload.revisions;
            state.usedInPathways = action.payload.usedInPathways;

            if (
                action.payload.elementAdminInfo.type === ELEMENT_TYPE_VIDEO_KEY ||
                action.payload.elementAdminInfo.type === ELEMENT_TYPE_PDF_KEY
            ) {
                state.uploadedResourcePreviewId = action.payload.elementAdminInfo.contentId;
            }
            if (action.payload.elementAdminInfo.type === ELEMENT_TYPE_YOU_TUBE_KEY) {
                state.uploadedResourcePreviewUrl = action.payload.elementAdminInfo.videoURI;
            }
        });
        builder.addCase(getCreatorElementById.rejected, (state, action) => {
            state.isGetCreatorElementPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createCreatorElement.pending, (state) => {
            state.isCreateCreatorElementPending = true;
            state.error = null;
        });
        builder.addCase(createCreatorElement.fulfilled, (state, action) => {
            state.isCreateCreatorElementPending = false;
            state.selectedElement = action.payload.elementAdminInfo;
            state.elementQuestions = ensureQuestionsTemporaryLocalIds(
                action.payload.elementAdminInfo.itemQuizQuestions,
            );
            state.elementRevisions = action.payload.revisions;
        });
        builder.addCase(createCreatorElement.rejected, (state, action) => {
            state.isCreateCreatorElementPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateCreatorElement.pending, (state) => {
            state.isUpdateCreatorElementPending = true;
            state.error = null;
        });
        builder.addCase(updateCreatorElement.fulfilled, (state, action) => {
            state.isUpdateCreatorElementPending = false;
            state.selectedElement = action.payload.elementAdminInfo;
            state.elementQuestions = ensureQuestionsTemporaryLocalIds(
                action.payload.elementAdminInfo.itemQuizQuestions,
            );
            state.elementRevisions = action.payload.revisions;
        });
        builder.addCase(updateCreatorElement.rejected, (state, action) => {
            state.isUpdateCreatorElementPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteCreatorElement.pending, (state) => {
            state.isDeleteCreatorElementPending = true;
            state.error = null;
        });
        builder.addCase(deleteCreatorElement.fulfilled, (state) => {
            state.isDeleteCreatorElementPending = false;
        });
        builder.addCase(deleteCreatorElement.rejected, (state, action) => {
            state.isDeleteCreatorElementPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createElementResourceWithUploadProgress.pending, (state) => {
            state.isUploadResourcePending = true;
            state.error = null;
        });
        builder.addCase(createElementResourceWithUploadProgress.fulfilled, (state, action) => {
            state.uploadedResourcePreviewUrl = null;
            state.uploadedResourcePreviewId = null;
            if (!isEmpty(action.payload)) {
                state.uploadedResourcePreviewId = action.payload[0].contentId;
            }
            state.isUploadResourcePending = false;
        });
        builder.addCase(createElementResourceWithUploadProgress.rejected, (state, action) => {
            state.isUploadResourcePending = false;
            state.uploadedResourcePreviewUrl = null;
            state.uploadedResourcePreviewId = null;
            state.error = extractError(action);
        });
        // load video url from contentId via resolveContentUri for preview on creator elements
        builder.addCase(resolveContentUri.pending, (state) => {
            state.isFilePreviewLoading = true;
            state.uploadedResourcePreviewUrl = null;
            state.error = null;
        });
        builder.addCase(resolveContentUri.fulfilled, (state, action) => {
            state.uploadedResourcePreviewUrl = action.payload.url;
            state.isFilePreviewLoading = false;
        });
        builder.addCase(resolveContentUri.rejected, (state, action) => {
            state.isFilePreviewLoading = false;
            state.error = extractError(action);
        });

        builder.addCase(createElementThumbnailWithUploadProgress.pending, (state) => {
            state.isUploadPublicContentPending = true;
            state.error = null;
        });

        builder.addCase(createElementThumbnailWithUploadProgress.fulfilled, (state, action) => {
            state.uploadedThumbnailUri = null;
            state.uploadedThumbnailId = null;
            if (!isEmpty(action.payload)) {
                state.uploadedThumbnailId = action.payload[0].contentId;
                state.uploadedThumbnailUri = action.payload[0].outputUrl;
            }
            state.isUploadPublicContentPending = false;
        });

        builder.addCase(createElementThumbnailWithUploadProgress.rejected, (state, action) => {
            state.isUploadPublicContentPending = false;
            state.uploadedThumbnailUri = null;
            state.error = extractError(action);
        });

        builder.addCase(checkCreatorElementContentReady.pending, (state) => {
            state.error = null;
        });
        builder.addCase(checkCreatorElementContentReady.fulfilled, (state, action) => {
            if (state.selectedElement?.id.toString() === action.meta.arg.id) {
                state.selectedElement.isContentReady = action.payload;
            }
        });
        builder.addCase(checkCreatorElementContentReady.rejected, (state, action) => {
            state.error = extractError(action);
        });
    },
});

export const {
    setModalPathwayElements,
    resetModalPathwayElementsState,
    setSelectedElement,
    setIsArchiveElementModalOpened,
    setIsPublishElementModalOpened,
    setIsArchiveElementFailedModalOpened,
    setIsChangeElementTypeModalOpened,
    setUploadedResourcePreviewUrl,
    setUploadedResourcePreviewId,
    clearUploadedResourcePreview,
    clearSelectedElement,
    setIsThumbnailModalOpened,
    setIsThumbnailUploaded,
    setIsVideoPreviewModalOpened,
    setIsYouTubeVideoPreviewModalOpened,
    setQuestionCreatorOpened,
    addNewQuestion,
    addQuestionsFromCsv,
    updateQuestion,
    removeQuestion,
    setSelectedQuestion,
    setIsRemoveQuestionModalOpened,
    setIsUploadQuestionsCsvModalOpened,
    setUploadedQuestionsCsvFile,
    setUploadedQuestionsCsvFilename,
    clearQuestionsCsvImportState,
    setQuestionsCsvImportFailed,
    setQuestionsCsvImportErroneous,
    setUploadProgress,
    clearUploadProgress,
    setIsDeleteSubtaskModalOpened,
    setRatingScaleModalOpened,
    setTaskPreviewModalOpened,
    setUploadedTaskCsvFilename,
    clearTaskCsvImportState,
    setTaskCsvImportFailed,
    setTaskCsvImportErroneous,
    setIsTaskCsvFilePickerRequested,
} = creatorElementsSlice.actions;

export default creatorElementsSlice.reducer;
